import { PivotFieldsAggridT } from 'business/definitions';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { ICellRendererParams } from 'ag-grid-community';
import {
    BudgetPhases,
    BudgetSections,
    BudgetExpenseOrIncome,
    BudgetBys,
    InscriptionStatusCategory,
} from 'business/constants';
import {
    ANTENNA_FIELD,
    BUDGET_CODE_FIELD,
    BUDGET_LINE_ID,
    BUDGET_NAME_FIELD,
    CHAPTER_FIELD,
    DEPASSEMENT_METRIC,
    FUNCTION_FIELD,
    GESTIONNAIRE_CODE_FIELD,
    GESTIONNAIRE_FIELD,
    GESTIONNAIRE_SOFTWARE_ID,
    MANDATED_FIELD,
    NATURE_FIELD,
    NATURE_LABEL_FIELD,
    OPERATION_FIELD,
    OPERATION_NAME_FIELD,
    OPERATION_SOFTWARE_ID,
    ORG_CHART_FINANCE_CODE_FIELD,
    ORG_CHART_FINANCE_FIELD,
    ORG_CHART_FINANCE_NAME_FIELD,
    ORG_CHART_FINANCE_SOFTWARE_ID,
    REALIZED_FIELD,
    SECONDARY_BUDGET_AXIS_ELEMENT_CODE,
    SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD,
    SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID,
    VIEW_DETAILS,
    VOTED_FIELD,
    VOTED_TOTAL_FIELD,
} from './constants';
import { ProjectionVentilation } from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/helpers/types';

export type ResourceType = 'orgChartFinance' | 'gestionnaire';
export type ResourceNameField =
    | 'orgChartFinance.tree'
    | 'gestionnaire.name'
    | 'orgChartFinance.name';
export type ResourceSoftwareIdField =
    | 'orgChartFinance.softwareId'
    | 'gestionnaire.softwareId';
export type ResourceCodeField = 'orgChartFinance.code' | 'gestionnaire.code';

export type CadrageMetrics =
    | typeof VOTED_FIELD
    | typeof REALIZED_FIELD
    | typeof VOTED_TOTAL_FIELD
    | typeof MANDATED_FIELD;

export type CadrageConfigT = {
    exercice: number;
    metric: CadrageMetrics;
    yearOffset: number;
};

export type CadrageT = {
    resource: string;
    amount: number;
    chapter?: string;
};

export type BudgetProposalT = {
    id: number;
    name: string;
    budgetName: string;
    budgetCode?: string;
    budgetSoftwareId?: string;
    exercice: number;
    section: BudgetSections | null;
    phase: BudgetPhases;
    canResourceEdit: string[];
    canEditPluriannualProjections?: boolean;
    status?: string;
    ownerEmail?: string;
    by: BudgetBys;
    projectionBy: ProjectionVentilation.SoftwareIds[];
    pluriannualProjectionBy: ProjectionVentilation.SoftwareIds[];
    cadrageByChapter: boolean;
    displayPosition?: number | null;
};

export type ResourceSoftwareIdAndNameT = { softwareId: string; name: string };

export type BudgetProposalWithResourcesT = BudgetProposalT & {
    resources: ResourceSoftwareIdAndNameT[];
    hasCustomCadrage: boolean;
};

export type FieldSettingsT = {
    field: string;
    hidden: boolean;
    index: number;
    grouped: boolean;
    width: number;
    groupIndex: number | null;
};

export type BudgetLine = {
    [ANTENNA_FIELD]: string;
    budgetLine: string;
    [BUDGET_LINE_ID]: number;
    [CHAPTER_FIELD]: string;
    [FUNCTION_FIELD]: string;
    [NATURE_FIELD]: string;
    [NATURE_LABEL_FIELD]: string;
    [ORG_CHART_FINANCE_FIELD]: OrgChartFinanceTree;
};

export type ChapterT = {
    code: string;
    label: string;
    softwareId: string;
};

export type NatureT = {
    number: string;
    label: string;
    softwareId: string;
};

export type OperationT = {
    [OPERATION_FIELD]: string;
    [OPERATION_NAME_FIELD]: string;
    [OPERATION_SOFTWARE_ID]: string;
};

export type SecondaryBudgetAxisElementT = {
    [SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID]: string;
    [SECONDARY_BUDGET_AXIS_ELEMENT_CODE]: string;
    [SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD]: string;
};

export type DraftOperationT = {
    name: string;
    code: string;
    softwareId: string;
};

export type DraftOperationAssociationT = {
    budgetProposalName: string;
    budgetProposalId: number;
    gestionnaireName: string | undefined;
    gestionnaireSoftwareId: string | undefined;
    orgChartFinanceName: string | undefined;
    orgChartFinanceSoftwareId: string | undefined;
    budgetProposalPhase: string;
};

export type DetailedDraftOperationT = DraftOperationT & {
    arbitrationTotal: number;
    serviceProposalTotal: number;
    primitiveTotal: number;
    supplementaryBudgetTotal: number;
    arbitrageDmTotal: number;
    associations: DraftOperationAssociationT[];
    enrichedOperationId: string;
};

export enum OperationType {
    DRAFT = 'DRAFT',
    FINANCE = 'FINANCE',
}

export type TypedOperationT = OperationT & {
    type: OperationType;
};

export type BudgetProposalSettingsT = {
    id: number;
    resourceType: string;
    resourceSoftwareId: string;
    imputationDimension: string;
    expenseGrid: { columns: FieldSettingsT[] };
    analyseExpenseGrid: { columns: PivotFieldsAggridT };
    analyseIncomeGrid: { columns: PivotFieldsAggridT };
    projectsGrid: { columns: PivotFieldsAggridT };
    showEmptyBudgetLines: boolean;
};

type GestionnaireResourceWithCodeT = {
    [GESTIONNAIRE_FIELD]: string;
    [GESTIONNAIRE_SOFTWARE_ID]: string;
    [GESTIONNAIRE_CODE_FIELD]?: string;
};

type OrgChartFinanceWithNameResourceT = {
    [ORG_CHART_FINANCE_NAME_FIELD]: string;
    [ORG_CHART_FINANCE_SOFTWARE_ID]: string;
    [ORG_CHART_FINANCE_CODE_FIELD]: string;
};

type OrgChartFinanceTree = {
    value: string;
    label: string;
};

export type ResourceBudgetProposalAssociationT = {
    resource?: {
        resourceName: string;
        resourceSoftwareId: string;
    };
    budgetProposal: BudgetProposalT;
    canAddInscription?: boolean;
};

export type ResourceWithNameAndCodeT =
    | OrgChartFinanceWithNameResourceT
    | GestionnaireResourceWithCodeT;

export type StatusesSettingsT = Partial<
    Record<
        BudgetPhases,
        {
            [key: string]: InscriptionStatusCategory;
        }
    >
>;

export type ArgumentT = {
    id: number;
    title: string;
    budgetProposalId: number;
    budgetProposal: BudgetProposalT;
    // public token is used for PDF generation only
    publicToken: string | null;
    resourceName: string | null;
    resourceSoftwareId: string;
    layoutId: number;
};

export type BudgetT = {
    [BUDGET_CODE_FIELD]: string;
    [BUDGET_NAME_FIELD]: string;
};

// these settings are common to the budget proposal page
// and the page specific to an org chart (gestionnaire or finance org chart)
type BasicBudgetQuerySettingsT = {
    budgetProposals: BudgetProposalT[];
    hasExerciceRange: boolean;
    requestBy: BudgetBys;
    exerciceRange: number;
};

/**
these settings are specific to the page of request by an org chart selected in the budget proposal page
*/
type RequestQuerySettingsT = {
    selectedOrgChart: string | null;
    resourceSoftwareId: string | null;
    expenseOrIncome: BudgetExpenseOrIncome | null;
    addOperation: boolean;
    isCadrage: boolean;
    extraFilters: QueryFilterT[];
    orgChartFinanceTree: DictDataValueT[] | null;
    cadrageConfig?: CadrageConfigT;
};

export type QuerySettingsT = BasicBudgetQuerySettingsT & RequestQuerySettingsT;

export type CellRendererParams<
    DataT = unknown,
    ValueT = DataT,
> = ICellRendererParams<DataT, ValueT>;

export type InscriptionWithMetadata = {
    budgetProposalPhase?: BudgetPhases;
    inscription: InscriptionT;
};

export type BudgetVentilatedDataT = (OperationT | ResourceWithNameAndCodeT) & {
    budgetLineCount?: number;
    expense: number;
    income: number;
};

export type BudgetVentilatedDataWithCadrageT = BudgetVentilatedDataT & {
    cadrage: number;
    [DEPASSEMENT_METRIC]: ResponseDataValueT;
    [VIEW_DETAILS.VIEW]: string;
};

export type BudgetProposalResourceMetadataT = {
    budgetProposalName: string;
    phase: BudgetPhases;
    resourceName: string;
};
